import TrackingWidget from "@amzn/rttv_web_app/lib/components/molecules/trackingWidget";
import { DesignMode, EventOrder } from "@amzn/rttv_web_app/lib/shared/constants";
import React from "react";
import { useState } from "react";
import { ApiGetTrackingDetailsModel, ApiGetTrackingDetailsForSupplyChainModel } from "../shared/interfaces";
import { useQuery } from "react-query";
import { transformApiResponseToShipmentDetails } from "../utils/transformations/transformApiResponseToShipmentDetails";
import { DELIVERED, GET_TRACKING_DETAILS_QUERY_KEY, PACKAGE_DELIVERY } from "../shared/strings/constants";
import getDeliveryMessage from "../utils/getDeliveryMessage";
import getFooterMessage from "../utils/getFooterMessage";
import { initialShipmentDetails } from "../shared/constants";
import { getShipmentDetailsRequest } from "../utils/actions";
import { RenderState } from "@amzn/rttv_web_app/lib/shared/constants";
import useClientInputs from "../hooks/useClientInputs";
import { getProgressLabelEnum } from "../utils/getProgressLabelEnum";
import logger from "../services/loggerService";
import useToken from "../hooks/useToken";
import { transformSupplyChainApiResponseToShipmentDetails } from "../utils/transformations/transformSupplyChainApiResponseToShipmentDetails";
import { getLogsiticsModeEnum } from "../utils/getLogisticsModeEnum";
import { strToBool } from "../utils/strToBool";
import { useTranslation } from "../i18n/useTranslation";

const TrackingWidgetContainer = () => {
    const { clientInput, isClientInputValid } = useClientInputs();
    const { AuthToken, APIKey } = useToken(clientInput);
    const [shipmentDetails, setShipmentDetails] = useState(initialShipmentDetails);
    const [apiError, setApiError] = useState(false);
    const t = useTranslation();
    const [errorMessage, setErrorMessage] = useState(t("rttv_widget_error_unable_to_provide_updates"));

    const shipmentDetailsRequest = async () => {
        const response = await getShipmentDetailsRequest(clientInput, AuthToken, APIKey);
        return response;
    };

    const { isLoading, isFetching, isError, refetch } = useQuery([GET_TRACKING_DETAILS_QUERY_KEY],
        shipmentDetailsRequest,
        {
            enabled: isClientInputValid,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchOnMount: false,
            onSuccess: (data) => {
                if ( data?.errorType ) {
                    logger.error(data);
                    setApiError(true);
                    setErrorMessage(t("rttv_widget_error_unable_to_provide_updates"));
                    return;
                }

                if(clientInput.logisticsMode === PACKAGE_DELIVERY) {
                    const shipmentInfo : ApiGetTrackingDetailsModel = JSON.parse(data);
                    if (shipmentInfo.trackingResponseAvailable === false) {
                        logger.error("No updates for tracking id", clientInput.trackingId);
                        setApiError(true);
                        setErrorMessage(t("rttv_widget_error_no_updates"));
                        return;
                    }
                    setApiError(false);
                    setShipmentDetails(transformApiResponseToShipmentDetails(shipmentInfo, clientInput));
                } else {
                    let parsedData = data;
                    if(typeof parsedData === "string") {
                        parsedData = JSON.parse(parsedData);
                        if(typeof parsedData === "string") {
                            parsedData = JSON.parse(parsedData);
                        }
                    }

                    const shipmentInfo : ApiGetTrackingDetailsForSupplyChainModel = parsedData;

                    if (shipmentInfo.trackingResponseList[0].trackingResponseAvailable === false) {
                        logger.error("No updates for tracking id", clientInput.trackingId);
                        setApiError(true);
                        setErrorMessage(t("rttv_widget_error_no_updates"));
                        return;
                    }
                    setApiError(false);
                    setShipmentDetails(transformSupplyChainApiResponseToShipmentDetails(shipmentInfo, clientInput));
                }
            },
            onError: (err) => {
                logger.error("Error while fetching shipment details", err);
                setErrorMessage(t("rttv_widget_error_unable_to_provide_updates"));
            }
        });

   const getRenderState = () => {
       if(isLoading || isFetching) {
           return RenderState.LOADING;
       }
       if (!isClientInputValid || apiError || isError) {
           return RenderState.ERROR;
       }
       return RenderState.DATA;
   };

    return (
      <TrackingWidget
        theme={clientInput.theme}
        renderState={getRenderState()}
        errorMessage={errorMessage}
        onRefreshHandler={refetch}
        trackingId={clientInput.trackingId}
        carrierId={clientInput.carrierId}
        currentStatus={shipmentDetails?.currentStatus}
        logisticsMode={getLogsiticsModeEnum(clientInput?.logisticsMode)}
        progressLabel={getProgressLabelEnum(shipmentDetails?.currentStatus)}
        deliveryDateMessage={getDeliveryMessage(shipmentDetails?.currentStatus === DELIVERED, shipmentDetails?.edd, shipmentDetails?.shipmentEvents[0], clientInput.logisticsMode, t)}
        footerMessage={getFooterMessage(shipmentDetails?.currentStatus === DELIVERED, shipmentDetails?.edd, t)}
        shipmentEvents={shipmentDetails?.shipmentEvents}
        trackingEstimates={shipmentDetails?.trackingEstimates}
        supplyChainTrackingEvents={clientInput?.supplyChainTrackingEvents}
        t={t}
        progressTrackerDisplayOptions={{
            designMode: clientInput.designMode === DesignMode.KATAL ?  DesignMode.KATAL : DesignMode.MERIDIAN,
            displayHorizontalProgressBar: strToBool(clientInput.displayHorizontalProgressBar),
            displayVerticalProgressBar: strToBool(clientInput.displayVerticalProgressBar),
            displayHeader: strToBool(clientInput.displayHeader),
            displayFooter: strToBool(clientInput.displayFooter),
            displayRefreshStatus: strToBool(clientInput.displayRefreshStatus),
            orderOfEvents: clientInput.orderOfEvents === EventOrder.ASCENDING ? EventOrder.ASCENDING : EventOrder.DESCENDING,
            height: clientInput.height,
            width: clientInput.width,
            compactMode: strToBool(clientInput.compactMode),
            eventCodeFont: clientInput.eventCodeFont,
            eventDescriptionFont: clientInput.eventDescriptionFont,
            eventTimeFont: clientInput.eventTimeFont
        }}
        />
    );
};

export default TrackingWidgetContainer;
